<script lang="ts" setup>
import { useBreadcrumbs } from '~layers/composables/useBreadcrumbs.ts'

const route = useRoute()
const { public: config, app } = useRuntimeConfig()
const { $t } = useI18n()

const meta = inject('meta', ref())

const {
  // data,
  m2Product,
  esProduct,
  parentCategories,
  // crosssellProducts,
  // upsellProducts,
  // relatedProducts,
  contentBlocks,
  usps,
  shopTheLook,
  deliveryAndReturns,
  stockAndDelivery,
  bynderAssets,
  fetchProductData,
  fetchFixedContent,
  fetchDynamicContent,
} = usePDP(meta)

const locale = config.strapiLocale
const status = 'PUBLISHED'

await Promise.all([
  fetchProductData({
    sku: meta.value?.sku,
  }),
  fetchFixedContent({
    locale,
    status,
  }),
  fetchDynamicContent({
    sku: meta.value?.sku,
    locale,
    status,
  }),
])

const selectedProductPrice = ref<{ regularPrice?: number, finalPrice?: number } | null>(null)

const productMediaGallery = computed(() => {
  return [
    ...(m2Product?.value?.media_gallery || []),
  ]
})

const attributeGroups = computed(() => {
  return m2Product?.value.attributeGroups?.map((group: any) => {
    return {
      ...group,
      attributes: group.attributes.filter((attribute: { value: any }) => attribute.value),
    }
  }) || []
})
// const productInfo = computed(() => {
//   const dataInfo = {
//     name: m2Product?.value?.name,
//   }
//   return dataInfo
// })

const productPrices = computed(() => {
  return selectedProductPrice.value || {
    regularPrice: m2Product.value?.price_range?.minimum_price?.regular_price?.value,
    finalPrice: m2Product.value?.price_range?.minimum_price?.final_price?.value,
  }
})

// const hasMultiplePrices = computed(() => {
//   if (m2Product.value?.__typename === 'ConfigurableProduct') {
//     const prices = m2Product.value?.variants?.map(({ product }: { product: MagentoProduct }) => product.price_range?.minimum_price?.final_price?.value) || []
//     return prices.some((price: number) => price !== prices[0])
//   }
//   return false
// })
//
// const isOos = computed(() => {
//   if (m2Product.value?.__typename === 'SimpleProduct') {
//     return m2Product.value.stock_status === 'OUT_OF_STOCK'
//   }
//   if (m2Product.value?.__typename === 'ConfigurableProduct') {
//     if (m2Product.value?.variants.every(item => item.product?.stock_status === 'OUT_OF_STOCK')) {
//       return true
//     }
//     // const selectedItemsArray = Object.values(selectedItems.value)
//     // if (selectedItemsArray.filter(item => item).length < selectedItemsArray.length) {
//     //   return true
//     // }
//   }
//   return false
// })
// })

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: esProduct?.value?.mainTitle,
  parents: [...parentCategories.value].sort((a, b) => a.level - b.level),
})

const { pageView, viewItem } = useDataLayer()
pageView('detail', esProduct?.value?.name)
viewItem(
  esProduct?.value,
  { skus: m2Product.value?.variants?.map((variant: { product: { sku: string } }) => variant.product.sku) || [esProduct.value?.sku] },
)

const metaTitle = computed(
  () => esProduct?.value?.name + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => $t(`meta.pdp.description`, {
    name: esProduct?.value?.name,
    sku: esProduct?.value?.sku,
  }) as string,
)

// const combinedRelatedProducts = computed<EsProduct[]>(() => {
//   return [
//     ...new Map([
//       ...(esProduct.value?.relatedProducts || []),
//       ...(relatedProducts.value || []),
//     ].map(item => [item.sku, item])).values(),
//   ]
// })

useHead({
  title: metaTitle,
  meta: [
    { key: 'robots', name: 'robots', content: esProduct.value?.saleType === 'OUTLET' ? 'noindex, follow' : 'index, follow' },
    { key: 'description', name: 'description', content: metaDescription },
  ],
})

// const { productSnippet, canonical } = useSeo()
// productSnippet(esProduct?.value)

const { canonical } = useSeo()
canonical(computed(() => route.path))

useSchemaOrg([
  defineProduct({
    'data-nosnippet': true,
    'name': `${esProduct?.value?.name} ${esProduct?.value?.descriptiveName}`,
    'image': `${config.xcdnUrl}OQ75_RM830,1066,255,255,255/-/catalog/product${esProduct?.value?.image}`,
    'sku': esProduct?.value?.sku,
    'brand': {
      name: esProduct.value?.brand?.label,
      logo: `${config.xcdnUrl}${esProduct.value?.brand?.swatch}`,
    },
    'offers': {
      url: `${config.baseDomain}${app.baseURL}${esProduct.value?.url}`.replace(/\/+/g, '/').replace(':/', '://'), // Safari compatible version
      priceCurrency: 'EUR',
      price: esProduct.value?.finalPrice || 0,
      itemCondition: 'https://schema.org/NewCondition',
      availability: `https://schema.org/${m2Product.value?.stock_status === 'OUT_OF_STOCK' ? 'OutOfStock' : 'InStock'}`,
    },
  }),
])

const additionalProductData = ref({})
const upsell = computed(() => {
  return additionalProductData.value.relatedProducts?.items?.find((item: any) => item.position.includes('upsell'))
})
onMounted(async () => {
  const result = await useGraphqlQuery('additionalProductData', {
    sku: esProduct?.value?.sku,
  }, useGraphqlDefaultClient())
  additionalProductData.value = result.data?.products?.items?.[0] || {}
})

// const detailsContainer = ref<HTMLElement | null>(null)

// const productDimensionsAndDetailsDialog = useUiDialog('dimensions-and-details-dialog')

const galleryName = 'media-gallery'
const mediaGalleryEnlarge = useUiDialog(galleryName)
const productDimensionsAndDetailsDialog = useUiDialog('dimensions-and-details-dialog')
const mediaARand3DDialog = useUiDialog('media-ar-and-3d')
const { threeDModel } = useBynderAssets(esProduct as ComputedRef)

provide('m2Product', m2Product)
provide('esProduct', esProduct)
provide('attributeGroups', attributeGroups)
provide('bynderAssets', bynderAssets)
provide('deliveryAndReturns', deliveryAndReturns)
provide('stockAndDelivery', stockAndDelivery)
</script>

<template>
  <div class="pdp">
    <AppBreadcrumbs />

    <div class="pdp__container">
      <!-- <AppHistoryBack class="pdp__back--link" /> -->

      <div class="pdp__layout">
        <div class="pdp__media">
          <UiMediaGallery
            :class="{
              'media-gallery--5+': productMediaGallery?.length > 5,
              'media-gallery--special': ['Banken'].includes(esProduct.groupCode),
            }"
            :name="galleryName"
            :options="{
              navigation: true,
              thumbs: true,
              dots: true,
              XofY: true,
              thumbsTrigger: 'click',
              fullscreen: true,
              limit: 5,
              zoom: false,
              showAll: false,
              fullscreenOptions: {
                navigation: true,
                thumbs: true,
                dots: true,
                XofY: true,
                zoom: false,
                thumbsTrigger: 'click',
              },
              media: productMediaGallery?.map(entry => ({
                src: entry.url,
                alt: esProduct?.name,
                provider: 'xcdn',
              })) || [],
            }"
          >
            <template #gallery-items>
              <div class="product-gallery__controls">
                <button
                  v-if="attributeGroups?.length"
                  type="button"
                  class="product-gallery__button"
                  @click="productDimensionsAndDetailsDialog.open()"
                >
                  <NuxtIcon name="icon:ruler" />
                  <span class="product-gallery__label">{{ $t('Dimensions and Details') }}</span>
                </button>

                <button
                  v-if="threeDModel.glb"
                  type="button"
                  class="product-gallery__button"
                  @click="mediaARand3DDialog.open()"
                >
                  <NuxtIcon name="icon:3d-arrows" />
                  <span class="product-gallery__label">{{ $t('View in 3D') }}</span>
                </button>

                <LazyProductARand3DDialog v-if="threeDModel.glb" />

                <button
                  type="button"
                  class="product-gallery__button"
                  @click="mediaGalleryEnlarge.open()"
                >
                  <NuxtIcon name="icon:zoom-in" />
                  <span class="product-gallery__label">{{ $t('Enlarge') }}</span>
                </button>
              </div>

              <div class="pdp__labels">
                <ProductLabelSale
                  :product="esProduct"
                />
                <ProductLabelMarketing
                  :product="esProduct"
                />
              </div>
            </template>
          </UiMediaGallery>
        </div>

        <div class="pdp__shopping">
          <!--
          <div class="pdp__rating">
            <ReviewStars :rating="3.5" />
            (12)
          </div>
          -->

          <div class="pdp__info">
            <UiHeading
              force-tag="h1"
              class="pdp__heading"
            >
              <span class="pdp__title">
                {{ esProduct?.mainTitle }}
              </span>
              <span class="pdp__brand">
                {{ esProduct?.subTitle }}
              </span>
            </UiHeading>

            <ProductToggleWishlist
              class="pdp__wishlist"
              :product="esProduct"
            />
          </div>

          <div class="pdp__pricing">
            <UiPrice
              v-if="productPrices"
              :prices="productPrices"
              class="pdp__price"
            />

            <ProductPriceIn3
              v-if="productPrices && esProduct.saleType !== 'OUTLET'"
              :prices="productPrices"
              class="pdp__price-in-3"
            />
          </div>

          <UiHeadingSection>
            <ProductVariants
              :product="esProduct"
            />

            <ProductStock
              v-if="additionalProductData.store_stock"
              :product="esProduct"
              :store-stock="additionalProductData.store_stock"
            />

            <ProductReservationButton
              v-if="esProduct.saleType === 'OUTLET'"
              class="pdp__reservation"
              :product="esProduct"
              :store-stock="additionalProductData.store_stock"
              :label="$t('Reserve this product')"
            />

            <ProductAddToCart
              v-if="esProduct.saleType !== 'OUTLET' && m2Product"
              class="pdp__add-to-cart"
              :product="{ ...m2Product, ...additionalProductData }"
              :product-name="esProduct?.name"
              :product-prices="productPrices"
              @product-price="(payload) => { selectedProductPrice = payload }"
            />

            <LazyAppUsps
              v-if="usps"
              class="pdp__usps"
              :usps="usps"
            />

            <ProductOfferButton
              class="pdp__offer"
              :product="esProduct"
            />

            <div
              v-if="esProduct.saleType === 'OUTLET'"
              class="pdp__almost-gone"
            >
              <p><b>{{ $t('This product is available only at the specified store; obtaining it from another store is unfortunately not possible.') }}</b></p>
              <p>
                <i18n-t keypath="Easily reserve the product using the &quot;Reserve product&quot; button. The store will confirm the reservation and inform you of the next steps. Do you have any questions about this product? Please ask them via the {contactForm}.">
                  <template #contactForm>
                    <AppLink :to="$t('/contact/')">
                      {{ $t('contact form') }}
                    </AppLink>
                  </template>
                </i18n-t>
              </p>
            </div>

            <ProductCombineWith
              v-if="upsell"
              class="pdp__combine-with"
              :related-products="upsell"
            />

            <!-- <AppPaymentIcons class="pdp__payment-icons" /> -->
          </UiHeadingSection>
        </div>

        <UiHeadingSection>
          <div class="pdp__details">
            <div
              v-if="m2Product.short_description?.html"
              class="pdp__description"
              v-html="m2Product.short_description.html"
            />

            <LazyProductHighlights
              v-if="m2Product?.highlights"
              :highlights="m2Product?.highlights"
              class="pdp__product-highlights"
            />

            <ProductPanels class="pdp__product-panels" />

            <div
              v-if="shopTheLook"
              class="pdp__look"
            >
              <LookQuote
                :slice="{
                  Look: shopTheLook,
                  Quote: shopTheLook?.Title,
                  Description: shopTheLook?.Description,
                  Button: shopTheLook?.Button,
                }"
                :index="2"
              />
            </div>
          </div>
        </UiHeadingSection>
      </div>
    </div>

    <UiHeadingSection>
      <!--
      <StrapiSlice
        v-if="combineWithProducts?.length"
        class="pdp__sliders"
        :slice="{
          Settings: {
            BackgroundColor: '#F3ECE6',
            SliceWidth: 'full-width',
          },
        }"
        :index="0"
        variant="horizontal"
      >
        <ProductSlider
          v-if="upsellProducts?.length"
          :products="upsellProducts"
          class="pdp__upsell"
        >
          <UiHeading>
            {{ $t('Complete your look') }}
          </UiHeading>
        </ProductSlider>

        <ProductSlider
          v-if="crosssellProducts?.length"
          :products="crosssellProducts"
          class="pdp__crosssell"
        >
          <UiHeading>
            {{ $t('Crosssell Products') }}
          </UiHeading>
        </ProductSlider>

        <ProductSlider
          v-if="relatedProducts?.length"
          :products="relatedProducts"
          class="pdp__related"
        >
          <UiHeading>
            {{ $t('Related Products') }}
          </UiHeading>
        </ProductSlider>

        <ProductSlider
          :products="combineWithProducts"
          class="pdp__slider pdp__recently-viewed"
        >
          <UiHeading>
            {{ $t('Last viewed') }}
          </UiHeading>
        </ProductSlider>
      </StrapiSlice>
      -->

      <StrapiSlice
        class="pdp__sliders"
        :slice="{
          Settings: {
            BackgroundColor: '#F3ECE6',
            SliceWidth: 'layout-width',
          },
        }"
        :index="2"
        variant="horizontal"
      >
        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-1" />
      </StrapiSlice>

      <StrapiSlice
        class="pdp__sliders"
        :slice="{
          Settings: {
            BackgroundColor: '#F3ECE6',
            SliceWidth: 'layout-width',
          },
        }"
        :index="3"
        variant="horizontal"
      >
        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-2" />
      </StrapiSlice>

      <StrapiSlice
        class="pdp__sliders"
        :slice="{
          Settings: {
            BackgroundColor: '#F3ECE6',
            SliceWidth: 'layout-width',
          },
        }"
        :index="4"
        variant="horizontal"
      >
        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-3" />
      </StrapiSlice>

      <StrapiSlices
        v-if="contentBlocks?.Slices?.length"
        :slices="contentBlocks.Slices"
        :slice-offset="4"
      />

      <StrapiSlice
        v-if="m2Product?.sku && $config?.public?.flowboxKey"
        class="pdp__flowbox"
        :slice="{
          Settings: {
            BackgroundColor: '#fff',
            SliceWidth: 'full-width',
            Title: 'Binnenkijken bij',
            Description: $t('@pronto_wonen'),
          },
        }"
        :index="contentBlocks?.Slices?.length + 5"
        variant="horizontal"
      >
        <ThirdpartyFlowbox
          id="flowbox-pdp"
          :flowbox-key="$config?.public?.flowboxKey"
          :tags="[]"
          :product-id="m2Product?.sku"
        />
      </StrapiSlice>
    </UiHeadingSection>

    
  </div>
</template>

<style>
@import url("~layers/pages/~pdp.css");
</style>
